import { Component, DestroyRef, ElementRef, OnInit, viewChild, effect, ChangeDetectionStrategy, signal, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe, DecimalPipe, NgStyle } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { CampaignStatus, BotanicCampaignStatus, CampaignStatusSummary } from 'src/app/models';
import { GoogleAnalyticsService, MakingWavesLoaderService } from '../../../shared/services';
import { CampaignStatusColorPipe } from '../../../shared/pipes/campaign-status.pipe';
import { DialogCampaignStatusDetails } from '../dialog-campaign-status-details/dialog-campaign-status-details.component';
import { CampaignStatusService } from '../../services/campaign-status.service';
import { DialogCampaignCancel } from '../dialog-campaign-cancel/dialog-campaign-cancel.component';

@Component({
    selector: 'app-campaign-search',
    templateUrl: './campaign-search.component.html',
    styleUrls: ['./campaign-search.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
      DatePipe, DecimalPipe, NgStyle,
      MatTableModule, MatPaginatorModule, MatSortModule,
      MatFormFieldModule, MatInputModule, MatIconModule,
      MatButtonModule, MatProgressSpinnerModule, MatListModule,
      MatTooltipModule, CampaignStatusColorPipe
    ]
})

export class CampaignSearchComponent implements OnInit {

  private destroyRef = inject(DestroyRef);
  dataSource!: MatTableDataSource<CampaignStatus>;
  columnsToDisplay = ['id', 'friendlyName','ownerUnitName', 'applicationName', 'regionName', 'anchorType', 'statusName', 'createdDate', 'cancel'];
  expandedElement: undefined | null;
  isLoadingResults = signal(true);
  isLoadingReport = signal(false);
  isCancelingCampaign = signal(false);
  botanicCampaignStatus = signal<BotanicCampaignStatus | undefined>(undefined);
  refreshTick = signal(0);

  userInput = viewChild.required<ElementRef>('input');
  sort = viewChild(MatSort);
  paginator = viewChild(MatPaginator);

  constructor() {
    effect(() => {
      const sort = this.sort();
      if (sort && this.dataSource) {
        this.dataSource.sort = sort;
      }
    });
  }
  private activatedRoute = inject(ActivatedRoute);
  private apiService = inject(CampaignStatusService);
  private loaderService = inject(MakingWavesLoaderService);
  private ga = inject(GoogleAnalyticsService);
  public dialog = inject(MatDialog);


  ngOnInit(): void {
    const campaignId = this.activatedRoute.snapshot.queryParams['campaignId'];
    this.getCampaigns(campaignId);

    this.ga.sendEvent('campaign_search_enter', {
      module: 'campaign',
      component:'CampaignSearchComponent'
    });
  }

  downloadCampaignsReports() {
    this.isLoadingReport.set(true);
    this.apiService.getCampaignsReport().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (blob: Blob) => {
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = 'report.csv';
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        this.isLoadingReport.set(false);
      },
      error: (err) => {
        console.error("Download failed:", err);
        this.isLoadingReport.set(false);
      }
    });
  }

  refreshCampaignsData() {
    this.dataSource.data = [];
    this.getCampaigns(this.userInput().nativeElement.value);
  }
  
  getCampaigns(campaignId: string | null) {
    this.isLoadingResults.set(true);
    this.apiService.getCampaigns().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: data => {
        this.dataSource = new MatTableDataSource(data);
        this.dataSource.paginator = this.paginator()!;
        if (campaignId != null) {
          this.userInput().nativeElement.value = campaignId;
          let event = new KeyboardEvent('keyup', { 'bubbles': true });
          this.userInput().nativeElement.dispatchEvent(event);
        }
        this.isLoadingResults.set(false);
        this.loaderService.hideLoader();
      },
      error: err => {
        this.isLoadingResults.set(false);
        this.loaderService.hideLoader();
        console.error('Observable emitted an error: ' + err)
      },
    });
  }
  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  getAnchorStatusCounters(row: CampaignStatus | null | undefined): void {
    if (row == null) return;

    if (row.applicationId === 'FB' || row.applicationId === 'TGE' || row.applicationId === 'TK') {
      this.botanicCampaignStatus.set(undefined);
      this.apiService.getBotanicCampaignStatus(row.campaignId).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe({
        next: data => {
          this.botanicCampaignStatus.set(data);
        },
        error: err => {
          console.error('Observable emitted an error: ' + err);
        }
      });
    } else if (row.applicationId === 'TG' && row.campaignStatusSummary == undefined) {
      this.apiService.getAnchorStatusCounters(row.campaignId).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe({
        next: response => {
          row.campaignStatusSummary = response.body ? response.body : [];
          this.refreshTick.update(v => v + 1); // trigger CD since row mutation isn't tracked by signals
        },
        error: err => {
          console.error('Observable emitted an error: ' + err);
        }
      });
    }
  }

  refreshBotanicStatus(row: CampaignStatus): void {
    this.botanicCampaignStatus.set(undefined);
    this.apiService.getBotanicCampaignStatus(row.campaignId).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: data => {
        this.botanicCampaignStatus.set(data);
      },
      error: err => {
        console.error('Observable emitted an error: ' + err);
      }
    });
  }

  getCampaignStatusDetailsService(parent: CampaignStatus, row: CampaignStatusSummary) {

    this.loaderService.showLoader();
    this.apiService.getCampaignStatusDetails(parent.campaignId, row.status).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: data => {
        row.campaignStatusDetails = data
        if (row.campaignStatusDetails) {
          this.dialog.open(DialogCampaignStatusDetails, {
            width: '1200px',
            data: {
              result: row.campaignStatusDetails,
              campaignName: parent?.friendlyName,
              status: row.status
            },
          });
        }
        this.loaderService.hideLoader();
      },
      error: err => {
        console.error('Observable emitted an error: ' + err);
      }
    });
  }

  onCancelCampaignClick(event: Event, campaign: any): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(DialogCampaignCancel, {
      data: { campaign },
    });
    dialogRef.afterClosed().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(result => {
      if (result) {
        this.isCancelingCampaign.set(true);
        this.apiService.putCampaignStatusCancel(result.id).pipe(
          takeUntilDestroyed(this.destroyRef)
        ).subscribe({
          next: () => {
            this.refreshCampaignsData();
            this.isCancelingCampaign.set(false);
            this.isLoadingResults.set(false);
            this.loaderService.hideLoader();
          },
          error: err => {
            this.isCancelingCampaign.set(false);
            this.isLoadingResults.set(false);
            this.loaderService.hideLoader();
            console.error('Observable emitted an error: ' + err)
          },
        });
      }
    });

  }

}

